import React, { useState, useEffect } from 'react';
import { useBusinessPlan } from '../../App';
import { useCurrency } from '../../contexts/CurrencyContext';
import axios from 'axios';
import { Tooltip as InfoTooltip } from '../ui/Tooltip';
import { FileText, Download, Search, Filter, Calendar, ChevronDown, ChevronUp, Copy, Edit2, Trash2, Plus, X, Eye, TrendingUp, ArrowUp, ArrowDown } from 'lucide-react';
import * as XLSX from 'xlsx';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { 
  BarChart, Bar, 
  LineChart, Line, 
  PieChart, Pie, 
  AreaChart, Area,
  ComposedChart,
  Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, 
  ReferenceLine 
} from 'recharts';
import html2canvas from 'html2canvas';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DetailedReports = () => {
  const { currentPlan } = useBusinessPlan();
  const { formatCurrency: formatCurrencyContext } = useCurrency();
  const selectedPlan = currentPlan;
  const [activeTab, setActiveTab] = useState('data-explorer');
  
  // Data Explorer State
  const [dataType, setDataType] = useState('all');
  const [dateRange, setDateRange] = useState('last-month');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedProduct, setSelectedProduct] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedDepartment, setSelectedDepartment] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sortField, setSortField] = useState('date');
  const [sortDirection, setSortDirection] = useState('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);

  // Phase 2: Report Builder State
  const [dataCatalog, setDataCatalog] = useState([]);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [selectedSections, setSelectedSections] = useState([]);
  const [reportConfigName, setReportConfigName] = useState('');
  const [reportConfigDescription, setReportConfigDescription] = useState('');
  const [savedConfigurations, setSavedConfigurations] = useState([]);
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [showGenerateDialog, setShowGenerateDialog] = useState(false);
  const [selectedConfigForGenerate, setSelectedConfigForGenerate] = useState(null);
  const [metricPreviews, setMetricPreviews] = useState({}); // Live preview data
  const [metricColors, setMetricColors] = useState({}); // Phase 4: Color customization {sourceId: {metricId: color}}

  // Phase 3: Report Viewer State
  const [generatedReports, setGeneratedReports] = useState([]);
  const [selectedReport, setSelectedReport] = useState(null);
  const [reportsLoading, setReportsLoading] = useState(false);

  // Phase 5: Scheduled Reports State
  const [schedules, setSchedules] = useState([]);
  const [schedulesLoading, setSchedulesLoading] = useState(false);
  const [showScheduleDialog, setShowScheduleDialog] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState(null);
  const [scheduleHistory, setScheduleHistory] = useState({});
  
  // Phase 5: Schedule form state
  const [scheduleName, setScheduleName] = useState('');
  const [scheduleDescription, setScheduleDescription] = useState('');
  const [scheduleConfigId, setScheduleConfigId] = useState('');
  const [scheduleFrequency, setScheduleFrequency] = useState('daily');
  const [scheduleTime, setScheduleTime] = useState('17:00');
  const [scheduleRecipients, setScheduleRecipients] = useState('');
  const [scheduleStartDate, setScheduleStartDate] = useState(new Date().toISOString().split('T')[0]);
  const [scheduleEndDate, setScheduleEndDate] = useState('');

  // Date range quick filters
  const getDateRange = (range) => {
    const today = new Date();
    const start = new Date();
    const end = new Date();

    switch (range) {
      case 'this-month':
        start.setDate(1);
        break;
      case 'last-month':
        start.setMonth(start.getMonth() - 1);
        start.setDate(1);
        end.setDate(0);
        break;
      case 'this-quarter':
        const quarter = Math.floor(today.getMonth() / 3);
        start.setMonth(quarter * 3);
        start.setDate(1);
        break;
      case 'last-quarter':
        const lastQuarter = Math.floor(today.getMonth() / 3) - 1;
        start.setMonth(lastQuarter * 3);
        start.setDate(1);
        end.setMonth((lastQuarter + 1) * 3);
        end.setDate(0);
        break;
      case 'this-year':
        start.setMonth(0);
        start.setDate(1);
        break;
      case 'custom':
        return { start: customStartDate, end: customEndDate };
      default:
        start.setDate(today.getDate() - 30);
    }

    return {
      start: start.toISOString().split('T')[0],
      end: end.toISOString().split('T')[0]
    };
  };

  // Fetch data from API
  const fetchData = async () => {
    if (!selectedPlan?.id) return;

    setLoading(true);
    try {
      const range = getDateRange(dateRange);
      const params = {
        plan_id: selectedPlan.id,
        data_type: dataType,
        start_date: range.start,
        end_date: range.end,
        search: searchTerm,
        product_id: selectedProduct,
        category: selectedCategory,
        department: selectedDepartment
      };

      const response = await axios.get(`${API}/actual-data/reports/data-explorer`, { params });
      setData(response.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [selectedPlan?.id, dataType, dateRange, customStartDate, customEndDate]);

  // Apply search and filters
  const getFilteredData = () => {
    if (!data?.entries) return [];

    let filtered = data.entries;

    // Apply search
    if (searchTerm) {
      filtered = filtered.filter(entry =>
        entry.item?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        entry.category?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        entry.objective?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        entry.initiative?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    return filtered;
  };

  // Sort data
  const getSortedData = () => {
    const filtered = getFilteredData();
    
    return [...filtered].sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];

      if (sortField === 'date') {
        aVal = new Date(aVal || 0);
        bVal = new Date(bVal || 0);
      }

      if (sortDirection === 'asc') {
        return aVal > bVal ? 1 : -1;
      } else {
        return aVal < bVal ? 1 : -1;
      }
    });
  };

  // Paginate data
  const getPaginatedData = () => {
    const sorted = getSortedData();
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return sorted.slice(startIndex, endIndex);
  };

  const totalPages = Math.ceil((getFilteredData().length || 0) / itemsPerPage);

  // Sort handler
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  // Export to Excel
  const exportToExcel = () => {
    const exportData = getSortedData().map(entry => ({
      'Date': entry.date,
      'Type': entry.type,
      'Item': entry.item,
      'Category': entry.category,
      'Department': entry.department,
      'Quantity': entry.quantity,
      'Planned': entry.planned,
      'Actual': entry.actual,
      'Variance': entry.variance,
      'Variance %': entry.variance_pct.toFixed(2) + '%',
      'Objective': entry.objective,
      'Initiative': entry.initiative
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Data Explorer');
    
    const range = getDateRange(dateRange);
    const filename = `Data_Explorer_${range.start}_to_${range.end}.xlsx`;
    XLSX.writeFile(wb, filename);
  };

  // Export to PDF
  const exportToPDF = () => {
    const doc = new jsPDF('landscape');
    const range = getDateRange(dateRange);
    
    // Header
    doc.setFontSize(16);
    doc.text('Data Explorer Report', 14, 15);
    doc.setFontSize(10);
    doc.text(`Period: ${range.start} to ${range.end}`, 14, 22);
    doc.text(`Generated: ${new Date().toLocaleDateString()}`, 14, 28);

    // Summary
    if (data?.summary) {
      doc.setFontSize(12);
      doc.text('Summary', 14, 38);
      doc.setFontSize(10);
      doc.text(`Total Revenue: ${formatCurrencyContext(data.summary.total_revenue)}`, 14, 45);
      doc.text(`Total Cost: ${formatCurrencyContext(data.summary.total_cost)}`, 14, 52);
      doc.text(`Total Profit: ${formatCurrencyContext(data.summary.total_profit)}`, 14, 59);
      doc.text(`Profit Margin: ${data.summary.profit_margin.toFixed(2)}%`, 14, 66);
    }

    // Table
    const tableData = getSortedData().map(entry => [
      entry.date,
      entry.type,
      entry.item,
      entry.category,
      entry.quantity,
      formatCurrencyContext(entry.planned),
      formatCurrencyContext(entry.actual),
      formatCurrencyContext(entry.variance),
      `${entry.variance_pct.toFixed(1)}%`
    ]);

    autoTable(doc, {
      startY: 75,
      head: [['Date', 'Type', 'Item', 'Category', 'Qty', 'Planned', 'Actual', 'Variance', 'Var %']],
      body: tableData,
      theme: 'striped',
      styles: { fontSize: 8 },
      headStyles: { fillColor: [59, 130, 246] }
    });

    doc.save(`Data_Explorer_${range.start}_to_${range.end}.pdf`);
  };

  const formatCurrency = (value) => {
    return formatCurrencyContext(value || 0, currentPlan?.currency);
  };

  const getVarianceColor = (variance) => {
    if (variance > 0) return 'text-green-600';
    if (variance < 0) return 'text-red-600';
    return 'text-gray-600';
  };

  const getVarianceIcon = (variance) => {
    if (variance > 0) return '↑';
    if (variance < 0) return '↓';
    return '→';
  };

  // ============ PHASE 2: Report Builder Functions ============

  // Fetch data catalog
  const fetchDataCatalog = async () => {
    if (!selectedPlan?.id) return;
    
    setCatalogLoading(true);
    try {
      const response = await axios.get(`${API}/actual-data/reports/data-catalog?plan_id=${selectedPlan.id}`);
      setDataCatalog(response.data.data_sources || []);
    } catch (error) {
      console.error('Error fetching data catalog:', error);
    } finally {
      setCatalogLoading(false);
    }
  };

  // Fetch saved configurations
  const fetchSavedConfigurations = async () => {
    if (!selectedPlan?.id) return;
    
    try {
      const response = await axios.get(`${API}/actual-data/reports/configurations?plan_id=${selectedPlan.id}`);
      setSavedConfigurations(response.data.configurations || []);
    } catch (error) {
      console.error('Error fetching configurations:', error);
    }
  };

  // Load catalog when tab changes
  useEffect(() => {
    if (activeTab === 'report-builder') {
      fetchDataCatalog();
      fetchSavedConfigurations();
    }
    // Phase 5: Also fetch configurations for scheduled reports tab
    if (activeTab === 'scheduled-reports') {
      fetchSavedConfigurations();
    }
  }, [activeTab, selectedPlan?.id]);

  // Fetch live preview for a data source
  const fetchLivePreview = async (sourceId) => {
    if (!selectedPlan?.id || metricPreviews[sourceId]) return; // Already loaded
    
    try {
      const range = getDateRange(dateRange);
      const response = await axios.get(
        `${API}/actual-data/reports/data-catalog/${sourceId}/preview?plan_id=${selectedPlan.id}&start_date=${range.start}&end_date=${range.end}`
      );
      
      setMetricPreviews(prev => ({
        ...prev,
        [sourceId]: response.data.metrics_preview || {}
      }));
    } catch (error) {
      console.error('Error fetching preview:', error);
    }
  };

  // Toggle metric selection with live preview
  const toggleMetric = (sourceId, metricId) => {
    // Fetch preview data for this source if not already loaded
    fetchLivePreview(sourceId);
    
    setSelectedSections(prev => {
      const existing = prev.find(s => s.data_source === sourceId);
      
      if (existing) {
        const updatedMetrics = existing.selected_metrics.includes(metricId)
          ? existing.selected_metrics.filter(m => m !== metricId)
          : [...existing.selected_metrics, metricId];
        
        if (updatedMetrics.length === 0) {
          return prev.filter(s => s.data_source !== sourceId);
        }
        
        return prev.map(s => 
          s.data_source === sourceId 
            ? { ...s, selected_metrics: updatedMetrics }
            : s
        );
      } else {
        const source = dataCatalog.find(ds => ds.id === sourceId);
        const firstDimension = source?.dimensions?.[0]?.id || '';
        return [...prev, {
          data_source: sourceId,
          selected_metrics: [metricId],
          dimensions: [firstDimension], // Set first dimension by default
          chart_type: source?.default_chart || 'bar',
          filters: {},
          order: prev.length + 1
        }];
      }
    });
  };

  // Update chart type for a section
  const updateChartType = (sourceId, chartType) => {
    setSelectedSections(prev =>
      prev.map(s =>
        s.data_source === sourceId ? { ...s, chart_type: chartType } : s
      )
    );
  };

  // Phase 4: Update metric color
  const updateMetricColor = (sourceId, metricId, color) => {
    setMetricColors(prev => ({
      ...prev,
      [sourceId]: {
        ...(prev[sourceId] || {}),
        [metricId]: color
      }
    }));
    
    // Also update in selectedSections
    setSelectedSections(prev =>
      prev.map(s => {
        if (s.data_source === sourceId) {
          return {
            ...s,
            metric_colors: {
              ...(s.metric_colors || {}),
              [metricId]: color
            }
          };
        }
        return s;
      })
    );
  };

  // Get color for a metric
  const getMetricColor = (sourceId, metricId, index) => {
    const DEFAULT_COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];
    return metricColors[sourceId]?.[metricId] || DEFAULT_COLORS[index % DEFAULT_COLORS.length];
  };

  // Update dimension for a section
  const updateDimension = (sourceId, dimensionId) => {
    setSelectedSections(prev =>
      prev.map(s =>
        s.data_source === sourceId ? { ...s, dimensions: [dimensionId] } : s
      )
    );
  };

  // Move section up
  const moveSectionUp = (sourceId) => {
    setSelectedSections(prev => {
      const index = prev.findIndex(s => s.data_source === sourceId);
      if (index <= 0) return prev;
      
      const newSections = [...prev];
      [newSections[index - 1], newSections[index]] = [newSections[index], newSections[index - 1]];
      
      // Update order numbers
      return newSections.map((s, idx) => ({ ...s, order: idx + 1 }));
    });
  };

  // Move section down
  const moveSectionDown = (sourceId) => {
    setSelectedSections(prev => {
      const index = prev.findIndex(s => s.data_source === sourceId);
      if (index < 0 || index >= prev.length - 1) return prev;
      
      const newSections = [...prev];
      [newSections[index], newSections[index + 1]] = [newSections[index + 1], newSections[index]];
      
      // Update order numbers
      return newSections.map((s, idx) => ({ ...s, order: idx + 1 }));
    });
  };

  // Save configuration
  const handleSaveConfiguration = async () => {
    if (!reportConfigName) {
      alert('Please enter a report name');
      return;
    }

    try {
      await axios.post(`${API}/actual-data/reports/configurations`, {
        plan_id: selectedPlan.id,
        name: reportConfigName,
        description: reportConfigDescription,
        sections: selectedSections
      });

      alert('Configuration saved successfully!');
      setShowSaveDialog(false);
      setReportConfigName('');
      setReportConfigDescription('');
      fetchSavedConfigurations();
    } catch (error) {
      console.error('Error saving configuration:', error);
      alert('Failed to save configuration');
    }
  };

  // Generate report from configuration
  const handleGenerateFromConfig = async () => {
    if (!selectedConfigForGenerate) return;

    try {
      const range = getDateRange(dateRange);
      
      await axios.post(`${API}/actual-data/reports/generate-from-config`, {
        config_id: selectedConfigForGenerate.id,
        date_range: {
          start: dateRange === 'custom' ? customStartDate : range.start,
          end: dateRange === 'custom' ? customEndDate : range.end
        },
        report_name: `${selectedConfigForGenerate.name} - ${new Date().toLocaleDateString()}`
      });

      alert('Report generated successfully!');
      setShowGenerateDialog(false);
      setSelectedConfigForGenerate(null);
    } catch (error) {
      console.error('Error generating report:', error);
      alert('Failed to generate report');
    }
  };

  // Delete configuration
  const handleDeleteConfiguration = async (configId) => {
    if (!window.confirm('Are you sure you want to delete this configuration?')) return;

    try {
      await axios.delete(`${API}/actual-data/reports/configurations/${configId}`);
      alert('Configuration deleted successfully!');
      fetchSavedConfigurations();
    } catch (error) {
      console.error('Error deleting configuration:', error);
      alert('Failed to delete configuration');
    }
  };

  // Load configuration for editing
  const handleLoadConfiguration = (config) => {
    setSelectedSections(config.sections || []);
    setReportConfigName(config.name);
    setReportConfigDescription(config.description);
    
    // Scroll to top to see the data sources
    window.scrollTo({ top: 0, behavior: 'smooth' });
    
    // Show a brief notification
    alert(`Configuration "${config.name}" loaded! Scroll up to see selected metrics.`);
  };

  // ============ PHASE 3: Report Viewer Functions ============

  // Fetch generated reports
  const fetchGeneratedReports = async () => {
    if (!selectedPlan?.id) return;
    
    setReportsLoading(true);
    try {
      const response = await axios.get(`${API}/actual-data/reports/generated?plan_id=${selectedPlan.id}`);
      setGeneratedReports(response.data.reports || []);
    } catch (error) {
      console.error('Error fetching generated reports:', error);
    } finally {
      setReportsLoading(false);
    }
  };

  // Load reports when tab changes
  useEffect(() => {
    if (activeTab === 'my-reports') {
      fetchGeneratedReports();
    }
  }, [activeTab, selectedPlan?.id]);

  // Phase 5: Fetch schedules when scheduled-reports tab is active
  useEffect(() => {
    if (activeTab === 'scheduled-reports') {
      fetchSchedules();
    }
  }, [activeTab, selectedPlan?.id]);

  // Phase 5: Populate form when editing schedule
  useEffect(() => {
    if (editingSchedule) {
      setScheduleName(editingSchedule.schedule_name || '');
      setScheduleDescription(editingSchedule.description || '');
      setScheduleConfigId(editingSchedule.report_config_id || '');
      setScheduleFrequency(editingSchedule.frequency || 'daily');
      setScheduleTime(editingSchedule.execution_time || '17:00');
      setScheduleRecipients(editingSchedule.recipients?.join(', ') || '');
      setScheduleStartDate(editingSchedule.start_date?.split('T')[0] || new Date().toISOString().split('T')[0]);
      setScheduleEndDate(editingSchedule.end_date?.split('T')[0] || '');
    }
  }, [editingSchedule]);

  // Phase 5: Fetch schedules
  const fetchSchedules = async () => {
    if (!selectedPlan?.id) return;

    setSchedulesLoading(true);
    try {
      const response = await axios.get(`${API}/actual-data/reports/schedules`);
      setSchedules(response.data.schedules || []);
    } catch (error) {
      console.error('Error fetching schedules:', error);
    } finally {
      setSchedulesLoading(false);
    }
  };

  // Phase 5: Fetch schedule history
  const fetchScheduleHistory = async (scheduleId) => {
    try {
      const response = await axios.get(`${API}/actual-data/reports/schedules/${scheduleId}/history`);
      setScheduleHistory(prev => ({
        ...prev,
        [scheduleId]: response.data.history || []
      }));
    } catch (error) {
      console.error('Error fetching schedule history:', error);
    }
  };

  // Phase 5: Delete schedule
  const handleDeleteSchedule = async (scheduleId) => {
    if (!window.confirm('Are you sure you want to delete this schedule?')) return;

    try {
      await axios.delete(`${API}/actual-data/reports/schedules/${scheduleId}`);
      alert('Schedule deleted successfully!');
      fetchSchedules();
    } catch (error) {
      console.error('Error deleting schedule:', error);
      alert('Failed to delete schedule');
    }
  };

  // Phase 5: Pause/Resume schedule
  const handleToggleSchedule = async (scheduleId, currentStatus) => {
    try {
      const action = currentStatus === 'active' ? 'pause' : 'resume';
      await axios.post(`${API}/actual-data/reports/schedules/${scheduleId}/${action}`);
      alert(`Schedule ${action}d successfully!`);
      fetchSchedules();
    } catch (error) {
      console.error('Error toggling schedule:', error);
      alert('Failed to update schedule');
    }
  };

  // Phase 5: Run schedule now
  const handleRunNow = async (scheduleId) => {
    if (!window.confirm('Run this scheduled report now?')) return;

    try {
      const response = await axios.post(`${API}/actual-data/reports/schedules/${scheduleId}/run-now`);
      alert(response.data.email_status || 'Report generated successfully!');
      fetchSchedules();
      fetchScheduleHistory(scheduleId);
    } catch (error) {
      console.error('Error running schedule:', error);
      alert('Failed to run schedule');
    }
  };

  // View report details
  const handleViewReport = async (reportId) => {
    try {
      const response = await axios.get(`${API}/actual-data/reports/generated/${reportId}`);
      setSelectedReport(response.data);
      // Switch to my-reports tab to show the report
      setActiveTab('my-reports');
    } catch (error) {
      console.error('Error fetching report:', error);
      alert('Failed to load report');
    }
  };

  // Delete generated report
  const handleDeleteReport = async (reportId) => {
    if (!window.confirm('Are you sure you want to delete this report?')) return;

    try {
      await axios.delete(`${API}/actual-data/reports/generated/${reportId}`);
      alert('Report deleted successfully!');
      fetchGeneratedReports();
      if (selectedReport?.id === reportId) {
        setSelectedReport(null);
      }
    } catch (error) {
      console.error('Error deleting report:', error);
      alert('Failed to delete report');
    }
  };

  // Chart colors
  const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];

  // Export report to Excel
  const exportReportToExcel = () => {
    if (!selectedReport) return;

    const workbook = XLSX.utils.book_new();

    // Summary sheet
    const summaryData = [
      ['Report Name', selectedReport.name],
      ['Configuration', selectedReport.configuration_name],
      ['Date Range', `${selectedReport.date_range?.start} to ${selectedReport.date_range?.end}`],
      ['Generated At', new Date(selectedReport.generated_at).toLocaleString()],
      ['Total Sections', selectedReport.sections?.length || 0],
      []
    ];
    const summarySheet = XLSX.utils.aoa_to_sheet(summaryData);
    XLSX.utils.book_append_sheet(workbook, summarySheet, 'Summary');

    // Create a sheet for each section
    selectedReport.sections?.forEach((section, idx) => {
      const sheetData = [];
      
      // Section header
      sheetData.push([section.data_source_label]);
      sheetData.push([]);
      
      // Metrics
      if (section.metrics) {
        sheetData.push(['Metrics']);
        Object.entries(section.metrics).forEach(([key, metric]) => {
          const value = metric.format === 'currency' ? `$${metric.value.toLocaleString()}` :
                       metric.format === 'percentage' ? `${metric.value}%` :
                       metric.value.toLocaleString();
          sheetData.push([metric.name, value]);
        });
        sheetData.push([]);
      }
      
      // Chart data
      if (section.chart_data && section.chart_data.length > 0) {
        sheetData.push(['Chart Data']);
        const headers = ['Name', ...Object.keys(section.metrics || {})];
        sheetData.push(headers);
        
        section.chart_data.forEach(dataPoint => {
          const row = [dataPoint.name];
          Object.keys(section.metrics || {}).forEach(metricKey => {
            row.push(dataPoint[metricKey] || 0);
          });
          sheetData.push(row);
        });
        sheetData.push([]);
      }
      
      sheetData.push(['Record Count', section.record_count]);
      
      const sheet = XLSX.utils.aoa_to_sheet(sheetData);
      const sheetName = section.data_source_label.substring(0, 31); // Excel sheet name limit
      XLSX.utils.book_append_sheet(workbook, sheet, sheetName);
    });

    // Download
    const fileName = `${selectedReport.name.replace(/[^a-z0-9]/gi, '_')}_${new Date().toISOString().split('T')[0]}.xlsx`;
    XLSX.writeFile(workbook, fileName);
  };

  // Export report to PDF
  const exportReportToPDF = async () => {
    if (!selectedReport) return;

    const doc = new jsPDF();
    let yPosition = 20;

    // Header
    doc.setFontSize(18);
    doc.text(selectedReport.name, 14, yPosition);
    yPosition += 10;

    doc.setFontSize(10);
    doc.text(`Date Range: ${selectedReport.date_range?.start} to ${selectedReport.date_range?.end}`, 14, yPosition);
    yPosition += 6;
    doc.text(`Generated: ${new Date(selectedReport.generated_at).toLocaleString()}`, 14, yPosition);
    yPosition += 10;

    // For each section
    for (let idx = 0; idx < (selectedReport.sections?.length || 0); idx++) {
      const section = selectedReport.sections[idx];
      
      // Check if we need a new page
      if (yPosition > 250) {
        doc.addPage();
        yPosition = 20;
      }

      // Section title
      doc.setFontSize(14);
      doc.text(section.data_source_label, 14, yPosition);
      yPosition += 8;

      // Metrics
      if (section.metrics) {
        doc.setFontSize(10);
        Object.entries(section.metrics).forEach(([key, metric]) => {
          const value = metric.format === 'currency' ? `$${metric.value.toLocaleString()}` :
                       metric.format === 'percentage' ? `${metric.value}%` :
                       metric.value.toLocaleString();
          doc.text(`${metric.name}: ${value}`, 14, yPosition);
          yPosition += 6;
        });
        yPosition += 4;
      }

      // Capture and add chart image
      if (section.chart_data && section.chart_data.length > 0) {
        try {
          // Find the chart element by index
          const chartElements = document.querySelectorAll('.recharts-wrapper');
          if (chartElements[idx]) {
            // Capture the chart as image
            const canvas = await html2canvas(chartElements[idx], {
              backgroundColor: '#ffffff',
              scale: 2
            });
            
            const imgData = canvas.toDataURL('image/png');
            
            // Check if we need a new page for the chart
            if (yPosition > 200) {
              doc.addPage();
              yPosition = 20;
            }
            
            // Add chart image to PDF
            const imgWidth = 170;
            const imgHeight = (canvas.height * imgWidth) / canvas.width;
            doc.addImage(imgData, 'PNG', 14, yPosition, imgWidth, imgHeight);
            yPosition += imgHeight + 10;
          }
        } catch (error) {
          console.error('Error capturing chart:', error);
          doc.setFontSize(8);
          doc.text('(Chart visualization not available)', 14, yPosition);
          yPosition += 6;
        }
      }

      // Check if we need a new page for the table
      if (yPosition > 220) {
        doc.addPage();
        yPosition = 20;
      }

      // Chart data table
      if (section.chart_data && section.chart_data.length > 0) {
        const tableData = section.chart_data.map(dataPoint => {
          const row = [dataPoint.name];
          Object.keys(section.metrics || {}).forEach(metricKey => {
            row.push((dataPoint[metricKey] || 0).toLocaleString());
          });
          return row;
        });

        const headers = ['Name', ...Object.keys(section.metrics || {}).map(k => 
          section.metrics[k].name.substring(0, 20)
        )];

        autoTable(doc, {
          startY: yPosition,
          head: [headers],
          body: tableData,
          theme: 'grid',
          styles: { fontSize: 8 },
          headStyles: { fillColor: [59, 130, 246] },
          margin: { left: 14 }
        });

        yPosition = doc.lastAutoTable.finalY + 10;
      }

      // Record count
      doc.setFontSize(8);
      doc.text(`Based on ${section.record_count} records`, 14, yPosition);
      yPosition += 10;
    }

    // Download
    const fileName = `${selectedReport.name.replace(/[^a-z0-9]/gi, '_')}_${new Date().toISOString().split('T')[0]}.pdf`;
    doc.save(fileName);
  };

  if (!selectedPlan) {
    return (
      <div className="p-6">
        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4">
          <p className="text-yellow-700">Please select a business plan to view detailed reports.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <InfoTooltip content="Comprehensive reporting and data analysis hub. 4 capabilities: (1) DATA EXPLORER = raw data browser with filtering/export, (2) REPORT BUILDER = visual report designer with charts, (3) MY REPORTS = report library, (4) SCHEDULED REPORTS = automated report delivery. Benefits: self-service analytics, consistency, automation, exportability.">
            <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
              <FileText className="w-8 h-8 text-blue-600" />
              Custom Reports
            </h1>
          </InfoTooltip>
          <p className="text-gray-600 mt-1">Explore, analyze, and export your performance data</p>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <InfoTooltip content="Raw data browser with filtering, search, sorting, and export. Features: date range filters, data type filters, advanced search, sort by any column, export to Excel/PDF. Use for: finding specific transactions, analyzing in Excel, verifying data accuracy, creating ad-hoc reports.">
            <button
              onClick={() => setActiveTab('data-explorer')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'data-explorer'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Data Explorer
            </button>
          </InfoTooltip>
          <InfoTooltip content="Visual report designer with metrics, charts, and insights. Build templates once, generate repeatedly with latest data. Workflow: Browse data catalog → Select metrics → Choose chart types → Customize → Save → Generate anytime! Use for: recurring formatted reports, consistent layouts, board/investor reports.">
            <button
              onClick={() => setActiveTab('report-builder')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'report-builder'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Report Builder
            </button>
          </InfoTooltip>
          <InfoTooltip content="Library of all generated reports. View, download (PDF/Excel), regenerate with latest data, or delete. Shows: report name, configuration used, generation date, quick actions. Use for: finding previous reports, downloading in different formats, regenerating with updated data, sharing with teammates. Tip: Use clear naming conventions.">
            <button
              onClick={() => setActiveTab('my-reports')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'my-reports'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              My Reports
            </button>
          </InfoTooltip>
          <InfoTooltip content="Automate report generation and email delivery on schedules (daily, weekly, monthly). Features: schedule configuration, frequency options, email recipients, run history. Workflow: Create schedule → Select report → Set frequency & time → Add recipients → Activate. Benefits: save time, consistency, never forget, scalability. Set it and forget it!">
            <button
              onClick={() => setActiveTab('scheduled-reports')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'scheduled-reports'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Scheduled Reports
            </button>
          </InfoTooltip>
        </nav>
      </div>

      {/* Data Explorer Tab */}
      {activeTab === 'data-explorer' && (
        <div className="space-y-6">
          {/* Date Range & Export Controls */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-4">
                <Calendar className="w-5 h-5 text-gray-500" />
                <h3 className="text-lg font-semibold">Date Range</h3>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={exportToExcel}
                  className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                  disabled={loading || !data?.entries?.length}
                >
                  <Download className="w-4 h-4" />
                  Excel
                </button>
                <button
                  onClick={exportToPDF}
                  className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                  disabled={loading || !data?.entries?.length}
                >
                  <Download className="w-4 h-4" />
                  PDF
                </button>
              </div>
            </div>

            {/* Quick Filters */}
            <div className="flex flex-wrap gap-2 mb-4">
              {['this-month', 'last-month', 'this-quarter', 'last-quarter', 'this-year', 'custom'].map((range) => (
                <button
                  key={range}
                  onClick={() => setDateRange(range)}
                  className={`px-4 py-2 rounded-lg border ${
                    dateRange === range
                      ? 'bg-blue-600 text-white border-blue-600'
                      : 'bg-white text-gray-700 border-gray-300 hover:border-blue-600'
                  }`}
                >
                  {range.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
                </button>
              ))}
            </div>

            {/* Custom Date Range */}
            {dateRange === 'custom' && (
              <div className="flex gap-4">
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                  <input
                    type="date"
                    value={customStartDate}
                    onChange={(e) => setCustomStartDate(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
                  <input
                    type="date"
                    value={customEndDate}
                    onChange={(e) => setCustomEndDate(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div className="flex items-end">
                  <button
                    onClick={fetchData}
                    className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  >
                    Apply
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Data Type Tabs */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center gap-4 border-b pb-4">
              {['all', 'sales', 'costs', 'objectives', 'initiatives'].map((type) => (
                <button
                  key={type}
                  onClick={() => setDataType(type)}
                  className={`px-4 py-2 rounded-lg font-medium ${
                    dataType === type
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {type.charAt(0).toUpperCase() + type.slice(1)}
                </button>
              ))}
            </div>

            {/* Smart Filters */}
            <div className="flex items-center gap-4 mt-4">
              <Filter className="w-5 h-5 text-gray-500" />
              <div className="flex-1 grid grid-cols-4 gap-4">
                <div>
                  <select
                    value={selectedProduct}
                    onChange={(e) => {
                      setSelectedProduct(e.target.value);
                      setCurrentPage(1);
                      fetchData();
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">All Products</option>
                    {data?.filters?.available_products?.map((product) => (
                      <option key={product} value={product}>{product}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <select
                    value={selectedCategory}
                    onChange={(e) => {
                      setSelectedCategory(e.target.value);
                      setCurrentPage(1);
                      fetchData();
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">All Categories</option>
                    {data?.filters?.available_categories?.map((category) => (
                      <option key={category} value={category}>{category}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <select
                    value={selectedDepartment}
                    onChange={(e) => {
                      setSelectedDepartment(e.target.value);
                      setCurrentPage(1);
                      fetchData();
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">All Departments</option>
                    {data?.filters?.available_departments?.map((dept) => (
                      <option key={dept} value={dept}>{dept}</option>
                    ))}
                  </select>
                </div>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search..."
                    value={searchTerm}
                    onChange={(e) => {
                      setSearchTerm(e.target.value);
                      setCurrentPage(1);
                    }}
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Summary Cards */}
          {data?.summary && (
            <div className="grid grid-cols-4 gap-4">
              <div className="bg-white rounded-lg shadow p-6">
                <p className="text-sm text-gray-600 mb-1">Total Revenue</p>
                <p className="text-2xl font-bold text-green-600">{formatCurrency(data.summary.total_revenue)}</p>
                <p className="text-xs text-gray-500 mt-1">Planned: {formatCurrency(data.summary.total_revenue_planned)}</p>
              </div>
              <div className="bg-white rounded-lg shadow p-6">
                <p className="text-sm text-gray-600 mb-1">Total Cost</p>
                <p className="text-2xl font-bold text-red-600">{formatCurrency(data.summary.total_cost)}</p>
                <p className="text-xs text-gray-500 mt-1">Budgeted: {formatCurrency(data.summary.total_cost_planned)}</p>
              </div>
              <div className="bg-white rounded-lg shadow p-6">
                <p className="text-sm text-gray-600 mb-1">Total Profit</p>
                <p className="text-2xl font-bold text-blue-600">{formatCurrency(data.summary.total_profit)}</p>
              </div>
              <div className="bg-white rounded-lg shadow p-6">
                <p className="text-sm text-gray-600 mb-1">Profit Margin</p>
                <p className="text-2xl font-bold text-purple-600">{data.summary.profit_margin.toFixed(1)}%</p>
              </div>
            </div>
          )}

          {/* Data Table */}
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="p-4 bg-gray-50 border-b flex items-center justify-between">
              <p className="text-sm text-gray-600">
                Showing {((currentPage - 1) * itemsPerPage) + 1} - {Math.min(currentPage * itemsPerPage, getFilteredData().length)} of {getFilteredData().length} entries
                {data?.summary && ` from ${data.summary.date_range}`}
              </p>
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600">Per page:</span>
                <select
                  value={itemsPerPage}
                  onChange={(e) => {
                    setItemsPerPage(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                  className="px-3 py-1 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                  <option value={200}>200</option>
                </select>
              </div>
            </div>

            {loading ? (
              <div className="p-12 text-center">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-gray-300 border-t-blue-600"></div>
                <p className="mt-2 text-gray-600">Loading data...</p>
              </div>
            ) : !data?.entries?.length ? (
              <div className="p-12 text-center">
                <p className="text-gray-600">No data found for the selected filters.</p>
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        {['date', 'type', 'item', 'category', 'department', 'quantity', 'planned', 'actual', 'variance'].map((field) => (
                          <th
                            key={field}
                            onClick={() => handleSort(field)}
                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                          >
                            <div className="flex items-center gap-1">
                              {field.charAt(0).toUpperCase() + field.slice(1).replace('_', ' ')}
                              {sortField === field && (
                                sortDirection === 'asc' ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />
                              )}
                            </div>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {getPaginatedData().map((entry, idx) => (
                        <tr key={idx} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{entry.date}</td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                              entry.type === 'Sales' ? 'bg-green-100 text-green-800' :
                              entry.type === 'Cost' ? 'bg-red-100 text-red-800' :
                              entry.type === 'Objective' ? 'bg-blue-100 text-blue-800' :
                              'bg-purple-100 text-purple-800'
                            }`}>
                              {entry.type}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-900">{entry.item}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{entry.category}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{entry.department}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{entry.quantity}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{formatCurrency(entry.planned)}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{formatCurrency(entry.actual)}</td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className={`text-sm font-semibold ${getVarianceColor(entry.variance)}`}>
                              {formatCurrency(entry.variance)} {getVarianceIcon(entry.variance)}
                              <span className="text-xs ml-1">({entry.variance_pct.toFixed(1)}%)</span>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="px-6 py-4 bg-gray-50 border-t flex items-center justify-between">
                    <button
                      onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                      disabled={currentPage === 1}
                      className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Previous
                    </button>
                    <div className="flex items-center gap-2">
                      {[...Array(Math.min(5, totalPages))].map((_, idx) => {
                        const page = idx + 1;
                        return (
                          <button
                            key={page}
                            onClick={() => setCurrentPage(page)}
                            className={`px-3 py-1 rounded-lg text-sm font-medium ${
                              currentPage === page
                                ? 'bg-blue-600 text-white'
                                : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                            }`}
                          >
                            {page}
                          </button>
                        );
                      })}
                      {totalPages > 5 && <span className="text-gray-500">...</span>}
                    </div>
                    <button
                      onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                      disabled={currentPage === totalPages}
                      className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Next
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* Report Builder Tab */}
      {activeTab === 'report-builder' && (
        <div className="space-y-6">
          {/* Header */}
          <div className="flex items-center justify-between bg-white rounded-lg shadow p-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Intelligent Report Builder</h2>
              <p className="text-gray-600 mt-1">Select metrics from your data and build dynamic reports</p>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setShowSaveDialog(true)}
                disabled={selectedSections.length === 0}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                Save Configuration
              </button>
            </div>
          </div>

          {catalogLoading ? (
            <div className="bg-white rounded-lg shadow p-12 text-center">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-gray-300 border-t-blue-600"></div>
              <p className="mt-2 text-gray-600">Loading data sources...</p>
            </div>
          ) : (
            <>
              {/* Selected Sections Summary */}
              {selectedSections.length > 0 && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h3 className="text-sm font-semibold text-blue-900 mb-2">
                    Selected Sections ({selectedSections.length}) - Drag to reorder
                  </h3>
                  <div className="space-y-2">
                    {selectedSections.map((section, idx) => {
                      const source = dataCatalog.find(ds => ds.id === section.data_source);
                      return (
                        <div key={section.data_source} className="bg-white border border-blue-300 rounded px-3 py-2 flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <span className="font-bold text-blue-600">#{idx + 1}</span>
                            <span className="font-medium">{source?.label || section.data_source}</span>
                            <span className="text-gray-600 text-sm">
                              ({section.selected_metrics.length} metrics, 
                              {section.dimensions?.[0] ? ` grouped by ${source?.dimensions?.find(d => d.id === section.dimensions[0])?.name}` : ' no grouping'})
                            </span>
                          </div>
                          <div className="flex gap-1">
                            <button
                              onClick={() => moveSectionUp(section.data_source)}
                              disabled={idx === 0}
                              className="p-1 hover:bg-blue-100 rounded disabled:opacity-30 disabled:cursor-not-allowed"
                              title="Move up"
                            >
                              <ArrowUp className="w-4 h-4 text-blue-600" />
                            </button>
                            <button
                              onClick={() => moveSectionDown(section.data_source)}
                              disabled={idx === selectedSections.length - 1}
                              className="p-1 hover:bg-blue-100 rounded disabled:opacity-30 disabled:cursor-not-allowed"
                              title="Move down"
                            >
                              <ArrowDown className="w-4 h-4 text-blue-600" />
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Data Sources Grid */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {dataCatalog.map((source) => {
                  const sectionData = selectedSections.find(s => s.data_source === source.id);
                  const selectedMetrics = sectionData?.selected_metrics || [];
                  
                  return (
                    <div key={source.id} className="bg-white rounded-lg shadow p-6">
                      {/* Source Header */}
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex items-center gap-2">
                          <span className="text-2xl">{source.icon}</span>
                          <div>
                            <h3 className="text-lg font-bold text-gray-900">{source.label}</h3>
                            <p className="text-sm text-gray-600">{source.description}</p>
                            {source.data_available && (
                              <p className="text-xs text-green-600 mt-1">
                                ✓ {source.record_count} records available
                              </p>
                            )}
                            {source.data_available === false && (
                              <p className="text-xs text-gray-400 mt-1">No data available</p>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Metrics Selection */}
                      <div className="mb-4">
                        <h4 className="text-sm font-semibold text-gray-700 mb-2">
                          Select Metrics ({selectedMetrics.length}/{source.metrics.length})
                        </h4>
                        <div className="space-y-2 max-h-48 overflow-y-auto">
                          {source.metrics.map((metric) => {
                            const preview = metricPreviews[source.id]?.[metric.id];
                            const isSelected = selectedMetrics.includes(metric.id);
                            
                            return (
                              <label
                                key={metric.id}
                                className={`flex items-start gap-2 p-2 rounded cursor-pointer transition-colors ${
                                  isSelected ? 'bg-blue-50 border border-blue-200' : 'hover:bg-gray-50'
                                }`}
                              >
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  onChange={() => toggleMetric(source.id, metric.id)}
                                  className="mt-1"
                                />
                                <div className="flex-1">
                                  <div className="flex items-center justify-between">
                                    <div className="font-medium text-sm text-gray-900">{metric.name}</div>
                                    {preview && isSelected && (
                                      <div className="text-sm font-bold text-green-600">
                                        {preview.format === 'currency' 
                                          ? formatCurrency(typeof preview.value === 'number' ? preview.value : 0)
                                          : preview.format === 'percentage'
                                          ? `${typeof preview.value === 'number' ? preview.value.toLocaleString() : preview.value}%`
                                          : typeof preview.value === 'number' ? preview.value.toLocaleString() : preview.value
                                        }
                                      </div>
                                    )}
                                  </div>
                                  <div className="text-xs text-gray-600">{metric.description}</div>
                                  <div className="text-xs text-blue-600 mt-1">
                                    Type: {metric.type} | Aggregation: {metric.aggregation}
                                  </div>
                                </div>
                              </label>
                            );
                          })}
                        </div>
                      </div>

                      {/* Dimension Selector */}
                      {selectedMetrics.length > 0 && source.dimensions && source.dimensions.length > 0 && (
                        <div className="mb-4">
                          <h4 className="text-sm font-semibold text-gray-700 mb-2">Group By (Dimension)</h4>
                          <select
                            value={sectionData?.dimensions?.[0] || ''}
                            onChange={(e) => updateDimension(source.id, e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                          >
                            {source.dimensions.map((dimension) => (
                              <option key={dimension.id} value={dimension.id}>
                                {dimension.name}
                              </option>
                            ))}
                          </select>
                        </div>
                      )}

                      {/* Chart Type Selector */}
                      {selectedMetrics.length > 0 && (
                        <div>
                          <h4 className="text-sm font-semibold text-gray-700 mb-2">Chart Type</h4>
                          <div className="flex flex-wrap gap-2">
                            {source.chart_types.map((chartType) => (
                              <button
                                key={chartType}
                                onClick={() => updateChartType(source.id, chartType)}
                                className={`px-3 py-1 rounded text-sm ${
                                  sectionData?.chart_type === chartType
                                    ? 'bg-blue-600 text-white'
                                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                }`}
                              >
                                {chartType}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Phase 4: Chart Color Customization */}
                      {selectedMetrics.length > 0 && (
                        <div className="mt-4">
                          <h4 className="text-sm font-semibold text-gray-700 mb-2">Chart Colors</h4>
                          <div className="space-y-2">
                            {source.metrics
                              .filter(metric => selectedMetrics.includes(metric.id))
                              .map((metric, index) => {
                                const currentColor = getMetricColor(source.id, metric.id, index);
                                return (
                                  <div key={metric.id} className="flex items-center gap-3 p-2 bg-gray-50 rounded">
                                    <input
                                      type="color"
                                      value={currentColor}
                                      onChange={(e) => updateMetricColor(source.id, metric.id, e.target.value)}
                                      className="w-10 h-8 rounded cursor-pointer border border-gray-300"
                                      title={`Choose color for ${metric.name}`}
                                    />
                                    <div className="flex-1">
                                      <div className="text-sm font-medium text-gray-900">{metric.name}</div>
                                      <div className="text-xs text-gray-500">{currentColor}</div>
                                    </div>
                                  </div>
                                );
                              })}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Saved Configurations */}
              {savedConfigurations.length > 0 && (
                <div className="bg-white rounded-lg shadow p-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-4">Saved Configurations ({savedConfigurations.length})</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {savedConfigurations.map((config) => (
                      <div key={config.id} className="border border-gray-200 rounded-lg p-4 hover:border-blue-500">
                        <div className="flex items-start justify-between mb-2">
                          <h4 className="font-semibold text-gray-900">{config.name}</h4>
                          <div className="flex gap-1">
                            <button
                              onClick={() => handleLoadConfiguration(config)}
                              className="p-1 hover:bg-gray-100 rounded"
                              title="Load"
                            >
                              <Edit2 className="w-4 h-4 text-gray-600" />
                            </button>
                            <button
                              onClick={() => handleDeleteConfiguration(config.id)}
                              className="p-1 hover:bg-red-100 rounded"
                              title="Delete"
                            >
                              <Trash2 className="w-4 h-4 text-red-600" />
                            </button>
                          </div>
                        </div>
                        <p className="text-sm text-gray-600 mb-3">{config.description}</p>
                        <div className="text-xs text-gray-500 mb-3">
                          {config.sections?.length || 0} sections • Updated {new Date(config.updated_at).toLocaleDateString()}
                        </div>
                        <button
                          onClick={() => {
                            setSelectedConfigForGenerate(config);
                            setShowGenerateDialog(true);
                          }}
                          className="w-full px-3 py-2 bg-green-600 text-white rounded text-sm hover:bg-green-700"
                        >
                          Generate Report
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Save Configuration Dialog */}
      {showSaveDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-gray-900">Save Report Configuration</h3>
              <button
                onClick={() => setShowSaveDialog(false)}
                className="p-1 hover:bg-gray-100 rounded"
              >
                <X className="w-5 h-5 text-gray-600" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Configuration Name</label>
                <input
                  type="text"
                  value={reportConfigName}
                  onChange={(e) => setReportConfigName(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., Monthly Sales & Cost Report"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description (Optional)</label>
                <textarea
                  value={reportConfigDescription}
                  onChange={(e) => setReportConfigDescription(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  rows="3"
                  placeholder="Describe what this report shows..."
                />
              </div>

              <div className="bg-gray-50 rounded p-3">
                <p className="text-sm text-gray-700">
                  <strong>{selectedSections.length}</strong> sections selected with{' '}
                  <strong>{selectedSections.reduce((sum, s) => sum + s.selected_metrics.length, 0)}</strong> total metrics
                </p>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowSaveDialog(false)}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveConfiguration}
                disabled={!reportConfigName}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300"
              >
                Save Configuration
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Generate Report Dialog */}
      {showGenerateDialog && selectedConfigForGenerate && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-gray-900">Generate Report</h3>
              <button
                onClick={() => setShowGenerateDialog(false)}
                className="p-1 hover:bg-gray-100 rounded"
              >
                <X className="w-5 h-5 text-gray-600" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Configuration</label>
                <p className="text-gray-900">{selectedConfigForGenerate.name}</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Date Range</label>
                <div className="space-y-2">
                  {['this-month', 'last-month', 'this-quarter', 'last-quarter', 'custom'].map((range) => (
                    <label key={range} className="flex items-center gap-2">
                      <input
                        type="radio"
                        name="generateDateRange"
                        value={range}
                        checked={dateRange === range}
                        onChange={(e) => setDateRange(e.target.value)}
                        className="text-blue-600"
                      />
                      <span className="text-sm text-gray-700">
                        {range.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
                      </span>
                    </label>
                  ))}
                </div>

                {dateRange === 'custom' && (
                  <div className="mt-3 grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Start Date</label>
                      <input
                        type="date"
                        value={customStartDate}
                        onChange={(e) => setCustomStartDate(e.target.value)}
                        className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">End Date</label>
                      <input
                        type="date"
                        value={customEndDate}
                        onChange={(e) => setCustomEndDate(e.target.value)}
                        className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowGenerateDialog(false)}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleGenerateFromConfig}
                className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
              >
                Generate →
              </button>
            </div>
          </div>
        </div>
      )}

      {/* My Reports Tab */}
      {activeTab === 'my-reports' && (
        <div className="space-y-6">
          {!selectedReport ? (
            <>
              {/* Header */}
              <div className="bg-white rounded-lg shadow p-6">
                <h2 className="text-2xl font-bold text-gray-900 mb-2">My Reports</h2>
                <p className="text-gray-600">View and manage your generated reports</p>
              </div>

              {reportsLoading ? (
                <div className="bg-white rounded-lg shadow p-12 text-center">
                  <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-gray-300 border-t-blue-600"></div>
                  <p className="mt-2 text-gray-600">Loading reports...</p>
                </div>
              ) : generatedReports.length === 0 ? (
                <div className="bg-white rounded-lg shadow p-12 text-center">
                  <TrendingUp className="w-16 h-16 mx-auto text-gray-400 mb-4" />
                  <p className="text-gray-600 text-lg">No reports generated yet</p>
                  <p className="text-gray-500 text-sm mt-2">Go to Report Builder to create and generate your first report!</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {generatedReports.map((report) => (
                    <div key={report.id} className="bg-white rounded-lg shadow hover:shadow-lg transition-shadow p-6">
                      <div className="flex items-start justify-between mb-3">
                        <h3 className="font-bold text-gray-900">{report.name}</h3>
                        <button
                          onClick={() => handleDeleteReport(report.id)}
                          className="p-1 hover:bg-red-100 rounded"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4 text-red-600" />
                        </button>
                      </div>
                      
                      <div className="space-y-2 mb-4">
                        <p className="text-sm text-gray-600">
                          <span className="font-medium">Configuration:</span> {report.configuration_name}
                        </p>
                        <p className="text-sm text-gray-600">
                          <span className="font-medium">Sections:</span> {report.sections?.length || 0}
                        </p>
                        <p className="text-sm text-gray-600">
                          <span className="font-medium">Date Range:</span> {report.date_range?.start} to {report.date_range?.end}
                        </p>
                        <p className="text-xs text-gray-500">
                          Generated: {new Date(report.generated_at).toLocaleString()}
                        </p>
                      </div>

                      <button
                        onClick={() => handleViewReport(report.id)}
                        className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                      >
                        <Eye className="w-4 h-4" />
                        View Report
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </>
          ) : (
            /* Report Viewer */
            <div className="space-y-6">
              {/* Header with Back Button */}
              <div className="bg-white rounded-lg shadow p-6">
                <button
                  onClick={() => setSelectedReport(null)}
                  className="text-blue-600 hover:text-blue-700 mb-4 flex items-center gap-2"
                >
                  ← Back to My Reports
                </button>
                <div className="flex items-start justify-between">
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">{selectedReport.name}</h2>
                    <p className="text-gray-600 mt-1">
                      {selectedReport.date_range?.start} to {selectedReport.date_range?.end}
                    </p>
                    <p className="text-sm text-gray-500 mt-1">
                      Generated: {new Date(selectedReport.generated_at).toLocaleString()}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <button 
                      onClick={exportReportToExcel}
                      className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2"
                    >
                      <Download className="w-4 h-4" />
                      Export Excel
                    </button>
                    <button 
                      onClick={exportReportToPDF}
                      className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center gap-2"
                    >
                      <Download className="w-4 h-4" />
                      Export PDF
                    </button>
                  </div>
                </div>
              </div>

              {/* Report Sections */}
              {selectedReport.sections?.map((section, idx) => (
                <div key={idx} className="bg-white rounded-lg shadow p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-4">{section.data_source_label}</h3>

                  {/* Metrics Cards */}
                  {section.metrics && Object.keys(section.metrics).length > 0 && (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                      {Object.entries(section.metrics).map(([key, metric]) => (
                        <div key={key} className="bg-gray-50 rounded-lg p-4">
                          <p className="text-sm text-gray-600 mb-1">{metric.name}</p>
                          <p className="text-2xl font-bold text-gray-900">
                            {metric.format === 'currency' 
                              ? formatCurrency(typeof metric.value === 'number' ? metric.value : 0)
                              : metric.format === 'percentage'
                              ? `${typeof metric.value === 'number' ? metric.value.toLocaleString() : metric.value}%`
                              : typeof metric.value === 'number' ? metric.value.toLocaleString() : metric.value
                            }
                          </p>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Chart */}
                  {section.chart_data && section.chart_data.length > 0 && (
                    <div className="mb-6">
                      <h4 className="text-sm font-semibold text-gray-700 mb-3">Visualization</h4>
                      <ResponsiveContainer width="100%" height={300}>
                        {section.chart_type === 'bar' && (
                          <BarChart data={section.chart_data}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="name" />
                            <YAxis tickFormatter={(value) => formatCurrencyContext(value, currentPlan?.currency, null, true)} />
                            <Tooltip formatter={(value) => formatCurrency(value)} />
                            <Legend />
                            {Object.keys(section.metrics || {}).map((metricKey, i) => {
                              const color = section.metric_colors?.[metricKey] || COLORS[i % COLORS.length];
                              return <Bar key={metricKey} dataKey={metricKey} fill={color} />;
                            })}
                          </BarChart>
                        )}
                        {section.chart_type === 'line' && (
                          <LineChart data={section.chart_data}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="name" />
                            <YAxis tickFormatter={(value) => formatCurrencyContext(value, currentPlan?.currency, null, true)} />
                            <Tooltip formatter={(value) => formatCurrency(value)} />
                            <Legend />
                            {Object.keys(section.metrics || {}).map((metricKey, i) => {
                              const color = section.metric_colors?.[metricKey] || COLORS[i % COLORS.length];
                              return <Line key={metricKey} type="monotone" dataKey={metricKey} stroke={color} strokeWidth={2} />;
                            })}
                          </LineChart>
                        )}
                        {section.chart_type === 'pie' && (
                          <PieChart>
                            <Pie
                              data={section.chart_data}
                              dataKey={Object.keys(section.metrics || {})[0]}
                              nameKey="name"
                              cx="50%"
                              cy="50%"
                              outerRadius={100}
                              label
                            >
                              {section.chart_data.map((entry, index) => {
                                const metricKey = Object.keys(section.metrics || {})[0];
                                const color = section.metric_colors?.[metricKey] || COLORS[index % COLORS.length];
                                return <Cell key={`cell-${index}`} fill={color} />;
                              })}
                            </Pie>
                            <Tooltip formatter={(value) => formatCurrency(value)} />
                            <Legend />
                          </PieChart>
                        )}
                        {/* Phase 4: Area Chart */}
                        {section.chart_type === 'area' && (
                          <AreaChart data={section.chart_data}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="name" />
                            <YAxis tickFormatter={(value) => formatCurrencyContext(value, currentPlan?.currency, null, true)} />
                            <Tooltip formatter={(value) => formatCurrency(value)} />
                            <Legend />
                            {Object.keys(section.metrics || {}).map((metricKey, i) => {
                              const color = section.metric_colors?.[metricKey] || COLORS[i % COLORS.length];
                              return (
                                <Area 
                                  key={metricKey} 
                                  type="monotone" 
                                  dataKey={metricKey} 
                                  stroke={color}
                                  fill={color}
                                  fillOpacity={0.6}
                                />
                              );
                            })}
                          </AreaChart>
                        )}
                        {/* Phase 4: Combo Chart (Bar + Line) */}
                        {section.chart_type === 'combo' && (
                          <ComposedChart data={section.chart_data}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="name" />
                            <YAxis tickFormatter={(value) => formatCurrencyContext(value, currentPlan?.currency, null, true)} />
                            <Tooltip formatter={(value) => formatCurrency(value)} />
                            <Legend />
                            {Object.keys(section.metrics || {}).map((metricKey, i) => {
                              const color = section.metric_colors?.[metricKey] || COLORS[i % COLORS.length];
                              // First metric as Bar, rest as Lines
                              if (i === 0) {
                                return <Bar key={metricKey} dataKey={metricKey} fill={color} />;
                              } else {
                                return <Line key={metricKey} type="monotone" dataKey={metricKey} stroke={color} strokeWidth={2} />;
                              }
                            })}
                          </ComposedChart>
                        )}
                        {/* Phase 4: Waterfall Chart */}
                        {section.chart_type === 'waterfall' && (
                          <BarChart data={section.chart_data}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="name" />
                            <YAxis tickFormatter={(value) => formatCurrencyContext(value, currentPlan?.currency, null, true)} />
                            <Tooltip formatter={(value) => formatCurrency(value)} />
                            <Legend />
                            {/* Waterfall bars showing incremental changes */}
                            {Object.keys(section.metrics || {}).map((metricKey, i) => {
                              const color = section.metric_colors?.[metricKey] || COLORS[i % COLORS.length];
                              return (
                                <Bar 
                                  key={metricKey} 
                                  dataKey={metricKey} 
                                  fill={color}
                                  stackId="waterfall"
                                />
                              );
                            })}
                          </BarChart>
                        )}
                      </ResponsiveContainer>
                    </div>
                  )}

                  {/* Data Summary */}
                  <p className="text-sm text-gray-500">
                    Based on {section.record_count} records
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Phase 5: Scheduled Reports Tab */}
      {activeTab === 'scheduled-reports' && (
        <div className="space-y-6">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold text-gray-900">Scheduled Reports</h2>
              <p className="text-sm text-gray-600 mt-1">
                Automate report generation and email delivery on a recurring schedule
              </p>
            </div>
            <button
              onClick={() => {
                setEditingSchedule(null);
                // Reset form
                setScheduleName('');
                setScheduleDescription('');
                setScheduleConfigId('');
                setScheduleFrequency('daily');
                setScheduleTime('17:00');
                setScheduleRecipients('');
                setScheduleStartDate(new Date().toISOString().split('T')[0]);
                setScheduleEndDate('');
                fetchSavedConfigurations(); // Ensure configs are loaded
                setShowScheduleDialog(true);
              }}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              Create Schedule
            </button>
          </div>

          {/* Schedules List */}
          {schedulesLoading ? (
            <div className="text-center py-12">
              <p className="text-gray-600">Loading schedules...</p>
            </div>
          ) : schedules.length === 0 ? (
            <div className="bg-white rounded-lg shadow p-12 text-center">
              <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No Scheduled Reports Yet</h3>
              <p className="text-gray-600 mb-6">
                Create your first scheduled report to automatically generate and email reports on a recurring basis
              </p>
              <button
                onClick={() => {
                  setEditingSchedule(null);
                  fetchSavedConfigurations(); // Ensure configs are loaded
                  setShowScheduleDialog(true);
                }}
                className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Create Your First Schedule
              </button>
            </div>
          ) : (
            <div className="grid gap-4">
              {schedules.map((schedule) => {
                const isActive = schedule.status === 'active';
                const history = scheduleHistory[schedule.id] || [];
                const lastExecution = history[0];
                
                return (
                  <div key={schedule.id} className="bg-white rounded-lg shadow p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="text-lg font-semibold text-gray-900">{schedule.schedule_name}</h3>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                          }`}>
                            {isActive ? 'Active' : 'Paused'}
                          </span>
                        </div>
                        {schedule.description && (
                          <p className="text-sm text-gray-600 mb-3">{schedule.description}</p>
                        )}
                        <div className="flex flex-wrap gap-4 text-sm text-gray-600">
                          <div className="flex items-center gap-2">
                            <Calendar className="w-4 h-4" />
                            <span className="capitalize">{schedule.frequency}</span>
                            {schedule.frequency === 'weekly' && <span>on Mondays</span>}
                            {schedule.frequency === 'monthly' && <span>on 1st</span>}
                            <span>at {schedule.execution_time}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <FileText className="w-4 h-4" />
                            <span>{schedule.recipients?.length || 0} recipients</span>
                          </div>
                        </div>
                        <div className="mt-2 text-xs text-gray-500">
                          Next run: {schedule.next_run_at ? new Date(schedule.next_run_at).toLocaleString() : 'N/A'}
                          {schedule.last_run_at && (
                            <span className="ml-4">
                              Last run: {new Date(schedule.last_run_at).toLocaleString()}
                            </span>
                          )}
                        </div>
                        {lastExecution && (
                          <div className={`mt-2 text-xs ${
                            lastExecution.status === 'success' ? 'text-green-600' : 'text-red-600'
                          }`}>
                            Last execution: {lastExecution.status}
                            {lastExecution.error_message && ` - ${lastExecution.error_message}`}
                          </div>
                        )}
                      </div>
                      
                      {/* Action Buttons */}
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleRunNow(schedule.id)}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg"
                          title="Run Now"
                        >
                          <TrendingUp className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => handleToggleSchedule(schedule.id, schedule.status)}
                          className={`p-2 rounded-lg ${
                            isActive 
                              ? 'text-yellow-600 hover:bg-yellow-50' 
                              : 'text-green-600 hover:bg-green-50'
                          }`}
                          title={isActive ? 'Pause' : 'Resume'}
                        >
                          {isActive ? '⏸️' : '▶️'}
                        </button>
                        <button
                          onClick={() => {
                            setEditingSchedule(schedule);
                            fetchSavedConfigurations(); // Ensure configs are loaded
                            setShowScheduleDialog(true);
                          }}
                          className="p-2 text-gray-600 hover:bg-gray-50 rounded-lg"
                          title="Edit"
                        >
                          <Edit2 className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => handleDeleteSchedule(schedule.id)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                          title="Delete"
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => {
                            if (!history.length) {
                              fetchScheduleHistory(schedule.id);
                            }
                          }}
                          className="p-2 text-gray-600 hover:bg-gray-50 rounded-lg"
                          title="View History"
                        >
                          <Eye className="w-5 h-5" />
                        </button>
                      </div>
                    </div>

                    {/* Execution History */}
                    {history.length > 0 && (
                      <div className="mt-4 border-t pt-4">
                        <h4 className="text-sm font-semibold text-gray-700 mb-3">Recent Executions</h4>
                        <div className="space-y-2">
                          {history.slice(0, 5).map((execution) => (
                            <div key={execution.id} className="flex items-center justify-between text-sm">
                              <div className="flex items-center gap-3">
                                <span className={`w-2 h-2 rounded-full ${
                                  execution.status === 'success' ? 'bg-green-500' : 'bg-red-500'
                                }`} />
                                <span className="text-gray-600">
                                  {new Date(execution.executed_at).toLocaleString()}
                                </span>
                                <span className={`px-2 py-0.5 rounded text-xs ${
                                  execution.status === 'success' 
                                    ? 'bg-green-100 text-green-800' 
                                    : 'bg-red-100 text-red-800'
                                }`}>
                                  {execution.status}
                                </span>
                                {execution.execution_type === 'manual' && (
                                  <span className="text-xs text-gray-500">(Manual)</span>
                                )}
                              </div>
                              {execution.report_id && (
                                <button
                                  onClick={() => handleViewReport(execution.report_id)}
                                  className="text-blue-600 hover:underline text-xs"
                                >
                                  View Report
                                </button>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Create/Edit Schedule Dialog */}
          {showScheduleDialog && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
                <div className="p-6 border-b border-gray-200">
                  <div className="flex items-center justify-between">
                    <h3 className="text-xl font-bold text-gray-900">
                      {editingSchedule ? 'Edit Schedule' : 'Create Schedule'}
                    </h3>
                    <button
                      onClick={() => {
                        setShowScheduleDialog(false);
                        setEditingSchedule(null);
                      }}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      <X className="w-6 h-6" />
                    </button>
                  </div>
                </div>

                <div className="p-6 space-y-6">
                  {/* Schedule Form */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Schedule Name *
                    </label>
                    <input
                      type="text"
                      value={scheduleName}
                      onChange={(e) => setScheduleName(e.target.value)}
                      placeholder="e.g., Monthly Executive Report"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Description
                    </label>
                    <textarea
                      value={scheduleDescription}
                      onChange={(e) => setScheduleDescription(e.target.value)}
                      placeholder="Optional description"
                      rows={2}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  {/* Report Configuration Selector - REBUILT FROM SCRATCH */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-900 mb-3">
                      Report Configuration *
                    </label>
                    
                    {savedConfigurations.length === 0 ? (
                      <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                        <p className="text-sm text-amber-800 font-medium">
                          ⚠️ No saved configurations found
                        </p>
                        <p className="text-xs text-amber-700 mt-1">
                          Please go to Report Builder tab and create a report configuration first.
                        </p>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {savedConfigurations.map((config) => (
                          <button
                            key={config.id}
                            type="button"
                            onClick={() => setScheduleConfigId(config.id)}
                            className={`w-full text-left p-4 rounded-lg border-2 transition-all ${
                              scheduleConfigId === config.id
                                ? 'border-blue-500 bg-blue-50'
                                : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50'
                            }`}
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex-1">
                                <p className="font-semibold text-gray-900 text-base">
                                  {config.name || 'Untitled Report'}
                                </p>
                                {config.description && (
                                  <p className="text-sm text-gray-600 mt-1">
                                    {config.description}
                                  </p>
                                )}
                                <p className="text-xs text-gray-500 mt-1">
                                  {config.sections?.length || 0} sections • 
                                  Created {new Date(config.created_at).toLocaleDateString()}
                                </p>
                              </div>
                              {scheduleConfigId === config.id && (
                                <div className="ml-3">
                                  <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center">
                                    <span className="text-white text-xs">✓</span>
                                  </div>
                                </div>
                              )}
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                    
                    {scheduleConfigId && (
                      <p className="text-sm text-green-600 mt-2 font-medium">
                        ✓ Configuration selected
                      </p>
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Frequency *
                      </label>
                      <select
                        value={scheduleFrequency}
                        onChange={(e) => setScheduleFrequency(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="daily">Daily (5pm)</option>
                        <option value="weekly">Weekly (Monday 5pm)</option>
                        <option value="monthly">Monthly (1st at 5pm)</option>
                        <option value="quarterly">Quarterly</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Execution Time
                      </label>
                      <input
                        type="time"
                        value={scheduleTime}
                        onChange={(e) => setScheduleTime(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Recipients (Email Addresses) *
                    </label>
                    <textarea
                      value={scheduleRecipients}
                      onChange={(e) => setScheduleRecipients(e.target.value)}
                      placeholder="email1@example.com, email2@example.com"
                      rows={2}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Enter email addresses separated by commas
                    </p>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Start Date
                      </label>
                      <input
                        type="date"
                        value={scheduleStartDate}
                        onChange={(e) => setScheduleStartDate(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        End Date (Optional)
                      </label>
                      <input
                        type="date"
                        value={scheduleEndDate}
                        onChange={(e) => setScheduleEndDate(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                </div>

                <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
                  <button
                    onClick={() => {
                      setShowScheduleDialog(false);
                      setEditingSchedule(null);
                    }}
                    className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={async () => {
                      if (!scheduleName || !scheduleConfigId || !scheduleRecipients) {
                        alert('Please fill in all required fields');
                        return;
                      }

                      const recipients = scheduleRecipients.split(',').map(e => e.trim()).filter(e => e);

                      if (recipients.length === 0) {
                        alert('Please enter at least one valid email address');
                        return;
                      }

                      const scheduleData = {
                        schedule_name: scheduleName,
                        description: scheduleDescription,
                        report_config_id: scheduleConfigId,
                        frequency: scheduleFrequency,
                        execution_time: scheduleTime,
                        recipients,
                        start_date: scheduleStartDate,
                        end_date: scheduleEndDate || null
                      };

                      console.log('Creating schedule with data:', scheduleData);

                      try {
                        if (editingSchedule) {
                          const response = await axios.put(`${API}/actual-data/reports/schedules/${editingSchedule.id}`, scheduleData);
                          console.log('Update schedule response:', response.data);
                          alert('Schedule updated successfully!');
                        } else {
                          const response = await axios.post(`${API}/actual-data/reports/schedules`, scheduleData);
                          console.log('Create schedule response:', response.data);
                          alert('Schedule created successfully!');
                        }
                        setShowScheduleDialog(false);
                        setEditingSchedule(null);
                        // Reset form
                        setScheduleName('');
                        setScheduleDescription('');
                        setScheduleConfigId('');
                        setScheduleFrequency('daily');
                        setScheduleTime('17:00');
                        setScheduleRecipients('');
                        setScheduleStartDate(new Date().toISOString().split('T')[0]);
                        setScheduleEndDate('');
                        fetchSchedules();
                      } catch (error) {
                        console.error('Error saving schedule:', error);
                        console.error('Error response:', error.response);
                        const errorMessage = error.response?.data?.detail || error.response?.data?.message || error.message || 'Unknown error';
                        alert('Failed to save schedule: ' + errorMessage);
                      }
                    }}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  >
                    {editingSchedule ? 'Update Schedule' : 'Create Schedule'}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default DetailedReports;
